import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bank, CheckCircle, Warning, Globe, ArrowsClockwise, CurrencyDollar, WarningCircle } from "@phosphor-icons/react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Sub-component for the "All set" view shown after a successful VietQR
 * attach. Includes a "I received a payment" affordance — the only
 * ground-truth signal we have that VietQR is actually working in the
 * field (since real scans bypass our servers).
 */
function VietQRDone({ done, token }) {
  const [mode, setMode] = useState(null); // null | 'confirm' | 'problem'
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [issueType, setIssueType] = useState("wrong_bank");
  const [issueDetail, setIssueDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedToday, setConfirmedToday] = useState(0);
  const [reportedToday, setReportedToday] = useState(0);

  const submitConfirm = async (e) => {
    e.preventDefault();
    const amt = Number(amount.replace(/[^0-9]/g, ""));
    if (!amt || amt < 1) {
      toast.error("Enter a VND amount greater than 0.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/api/public/vietqr-setup/${token}/confirm-payment`, {
        amount_vnd: amt,
        note: note.trim() || undefined,
      });
      toast.success(r.data.message || "Thanks — payment recorded.");
      setConfirmedToday((n) => n + 1);
      setAmount("");
      setNote("");
      setMode(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to record payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitProblem = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/api/public/vietqr-setup/${token}/report-problem`, {
        issue_type: issueType,
        detail: issueDetail.trim() || undefined,
      });
      toast.success(r.data.message || "Thanks — we'll review this.");
      setReportedToday((n) => n + 1);
      setIssueDetail("");
      setMode(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-emerald-200 p-7 text-center shadow-sm" data-testid="vietqr-setup-done">
        <span className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center">
          <CheckCircle size={32} weight="fill" className="text-emerald-600" />
        </span>
        <h1 className="serif text-3xl text-[#112318] mt-4">All set</h1>
        <p className="text-sm text-[#4A5C50] mt-3 leading-relaxed">{done.message}</p>
        <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-left">
          <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-700">Routing destination</p>
          <p className="font-semibold text-[#112318] mt-1" data-testid="done-bank-name">{done.bank_name}</p>
          <p className="text-sm text-[#4A5C50] mono mt-0.5">Account •••• {done.account_last4}</p>
        </div>
        <p className="text-xs text-[#4A5C50] mt-5 leading-relaxed">
          Any Vietnamese banking app (Vietcombank, BIDV, MB, Techcombank…) that scans your Zcardon
          card will now send VND directly to this bank account via the NAPAS network.
        </p>

        <div className="mt-6 pt-5 border-t border-emerald-100 text-left">
          {mode === null && (
            <div className="space-y-2">
              <button
                onClick={() => setMode("confirm")}
                className="w-full rounded-xl bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold py-3 inline-flex items-center justify-center gap-2"
                data-testid="confirm-payment-cta"
              >
                <CurrencyDollar size={18} weight="bold" /> I received a payment via this QR
              </button>
              <button
                onClick={() => setMode("problem")}
                className="w-full rounded-xl bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold py-2.5 inline-flex items-center justify-center gap-2 text-sm"
                data-testid="report-problem-cta"
              >
                <WarningCircle size={16} weight="bold" /> Report a problem with this card
              </button>
            </div>
          )}

          {mode === "confirm" && (
            <form onSubmit={submitConfirm} className="space-y-3" data-testid="confirm-payment-form">
              <p className="text-xs text-[#4A5C50] leading-relaxed">
                Confirming a payment helps your sender see this card is working. We only record the
                amount and an optional note — never bank details.
              </p>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  Amount (VND)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="125000"
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  data-testid="confirm-amount-input"
                  required
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  From (optional)
                </label>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 120))}
                  placeholder="from grandma"
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  data-testid="confirm-note-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting || !amount}
                  className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold py-2.5 inline-flex items-center justify-center gap-2"
                  data-testid="confirm-submit-btn"
                >
                  {submitting ? <ArrowsClockwise size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />}
                  Record payment
                </button>
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="text-sm text-[#4A5C50] hover:text-[#112318]"
                  data-testid="confirm-cancel-btn"
                >
                  Cancel
                </button>
              </div>
              {confirmedToday > 0 && (
                <p className="text-[11px] text-emerald-700 text-center" data-testid="confirmed-count">
                  ✓ {confirmedToday} payment{confirmedToday === 1 ? "" : "s"} recorded today
                </p>
              )}
            </form>
          )}

          {mode === "problem" && (
            <form onSubmit={submitProblem} className="space-y-3" data-testid="report-problem-form">
              <p className="text-xs text-[#4A5C50] leading-relaxed">
                Sorry to hear that. We'll review and reach out to your sender if needed.
              </p>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  What went wrong?
                </label>
                <select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-600"
                  data-testid="problem-type-select"
                >
                  <option value="wrong_bank">Wrong bank or account</option>
                  <option value="refused_at_merchant">Merchant wouldn't accept it</option>
                  <option value="scammed">I think I was scammed</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  More detail (optional)
                </label>
                <textarea
                  value={issueDetail}
                  onChange={(e) => setIssueDetail(e.target.value.slice(0, 500))}
                  placeholder="Tell us what happened…"
                  rows={3}
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-600 resize-none"
                  data-testid="problem-detail-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:bg-rose-600/50 text-white font-semibold py-2.5 inline-flex items-center justify-center gap-2"
                  data-testid="problem-submit-btn"
                >
                  {submitting ? <ArrowsClockwise size={16} className="animate-spin" /> : <WarningCircle size={16} weight="fill" />}
                  Submit report
                </button>
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="text-sm text-[#4A5C50] hover:text-[#112318]"
                  data-testid="problem-cancel-btn"
                >
                  Cancel
                </button>
              </div>
              {reportedToday > 0 && (
                <p className="text-[11px] text-rose-700 text-center" data-testid="reported-count">
                  ✓ {reportedToday} problem{reportedToday === 1 ? "" : "s"} reported today
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Public VietQR self-onboarding page.
 *
 * Receiver lands here via a tokenized link (`/vietqr-setup/:token`) sent
 * by the sender. They pick their bank, enter their account # + display
 * name, and submit — backend attaches VietQR to the card so the QR on
 * the printed/MMS'd card flips to a real EMVCo VietQR string scannable
 * by Vietnamese banking apps via NAPAS.
 */
export default function VietQRSetup() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [bankBin, setBankBin] = useState("970436");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [city, setCity] = useState("HA NOI");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const loadInfo = useCallback(async () => {
    try {
      const [infoRes, banksRes] = await Promise.all([
        axios.get(`${API_BASE}/api/public/vietqr-setup/${token}`),
        axios.get(`${API_BASE}/api/public/vietqr/banks`),
      ]);
      setInfo(infoRes.data);
      setBanks(banksRes.data.banks || []);
      // Prefill the account-holder field with the receiver's name if available.
      if (infoRes.data.receiver_name && !accountName) {
        setAccountName(String(infoRes.data.receiver_name).toUpperCase().slice(0, 25));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoadError(typeof detail === "string" ? detail : "Setup link could not be loaded.");
    }
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountNumber.trim() || !accountName.trim()) {
      toast.error("Bank account number and name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/api/public/vietqr-setup/${token}/submit`, {
        bank_bin: bankBin,
        account_number: accountNumber.replace(/\D/g, ""),
        account_name: accountName.toUpperCase().slice(0, 25),
        city: city.toUpperCase().slice(0, 15),
        service_type: "account",
      });
      setDone(r.data);
      toast.success(r.data.message || "VietQR attached.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- LOAD ERROR STATE ----
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-rose-200 p-6 text-center shadow-sm" data-testid="vietqr-setup-error">
          <Warning size={42} weight="duotone" className="mx-auto text-rose-500" />
          <h1 className="serif text-2xl text-[#112318] mt-3">Setup link issue</h1>
          <p className="text-sm text-[#4A5C50] mt-2">{loadError}</p>
          <Link to="/" className="inline-block mt-4 text-sm text-[#184A30] underline">Back to Zcardon home</Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <p className="text-[#4A5C50] text-sm">Loading…</p>
      </div>
    );
  }

  // ---- DONE / SUCCESS STATE ----
  if (done) {
    return <VietQRDone done={done} token={token} />;
  }

  // ---- ALREADY-ATTACHED INFO (re-onboarding) ----
  const alreadyBanner = info.already_attached && (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-amber-900 text-xs flex items-start gap-2 mb-4">
      <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold">This card already has VietQR attached</p>
        <p>
          Currently routes to <span className="font-semibold">{info.current_bank} •••• {info.current_last4}</span>.
          Submitting this form will REPLACE that routing with the new bank below.
        </p>
      </div>
    </div>
  );

  // ---- FORM STATE ----
  return (
    <div className="min-h-screen bg-[#FBF9F6] py-10 px-4">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <img src="/zcardon-logo.png" alt="Cardon" className="h-8" onError={(e) => (e.target.style.display = "none")} />
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-hidden">
          <div className="px-6 pt-6 pb-5 border-b border-[#E5E0D8]">
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">
              <Globe size={12} /> VietQR setup · NAPAS
            </span>
            <h1 className="serif text-2xl text-[#112318] mt-2" data-testid="vietqr-setup-heading">
              Hi {info.receiver_name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
              {info.sender_first_name ? <><span className="font-semibold">{info.sender_first_name}</span> sent you a Zcardon card</> : "You've been sent a Zcardon card"}
              {info.last4 ? <> ending <span className="mono font-semibold">{info.last4}</span></> : null}.
              Add your Vietnamese bank account below and the QR on your card will become a real
              VietQR — any banking app in Vietnam (Vietcombank, BIDV, MB, Techcombank…) can scan
              it and send VND straight to your bank.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4" data-testid="vietqr-setup-form">
            {alreadyBanner}

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                Your bank
              </label>
              <select
                value={bankBin}
                onChange={(e) => setBankBin(e.target.value)}
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid="setup-bank-select"
              >
                {banks.map((b) => (
                  <option key={b.bin} value={b.bin}>
                    {b.name} (BIN {b.bin})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                Account number
              </label>
              <input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 24))}
                placeholder="1900000000000"
                inputMode="numeric"
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid="setup-account-input"
                required
              />
              <p className="text-[11px] text-[#4A5C50] mt-1">Use the account # from your banking app. We never see your password.</p>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                Account name <span className="text-[#4A5C50]">(as it appears on your bank statement)</span>
              </label>
              <input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value.toUpperCase().slice(0, 25))}
                placeholder="NGUYEN VAN A"
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid="setup-name-input"
                required
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                City
              </label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value.toUpperCase().slice(0, 15))}
                placeholder="HA NOI"
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid="setup-city-input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !accountNumber || !accountName}
              className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/50 text-white font-semibold py-3 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="setup-submit-btn"
            >
              {submitting ? (
                <><ArrowsClockwise size={18} className="animate-spin" /> Setting up…</>
              ) : (
                <><Bank size={18} weight="bold" /> Finish setup</>
              )}
            </button>
            <p className="text-[11px] text-[#4A5C50] text-center mt-1">
              By submitting you confirm this is your bank account. Funds will route directly to it.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
